import React, { useEffect, useMemo, useState } from "react";
import {
  agruparPorBorda,
  type PainelBordeiroDia,
  type ProducaoBordaItem,
} from "../../../../services/painelBordeiroService";
import { formatDiaMes, formatDuracao } from "../../../PainelBordeiro/utils";
import { useTvAutoScroll } from "../hooks/useTvAutoScroll";

type Props = {
  dados: PainelBordeiroDia;
  offsetMs: number;
};

const TH =
  "whitespace-nowrap px-3 py-3 text-[clamp(0.75rem,1.4vw,1.6rem)] font-bold uppercase tracking-wide text-white/85 lg:px-6";
const TD =
  "whitespace-nowrap px-3 py-[clamp(0.35rem,1vh,1rem)] text-[clamp(1.1rem,2.6vw,3.5rem)] font-black tabular-nums leading-none lg:px-6";
const TD_NOME =
  "px-3 py-[clamp(0.35rem,1vh,1rem)] text-[clamp(1rem,2.2vw,3rem)] font-extrabold leading-tight lg:px-6";

function useAgora(ativo: boolean): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ativo]);
  return agora;
}

function tempoItem(item: ProducaoBordaItem, agoraServidor: number): number {
  const { status, rodandoDesde, tempoAcumuladoMs } = item.cronometro;
  return (
    tempoAcumuladoMs +
    (status === "rodando" && rodandoDesde
      ? agoraServidor - new Date(rodandoDesde).getTime()
      : 0)
  );
}

const ORDEM_STATUS: Record<ProducaoBordaItem["cronometro"]["status"], number> =
  { rodando: 0, pausado: 1, parado: 2, concluido: 3 };

const TvTabelaDia: React.FC<Props> = ({ dados, offsetMs }) => {
  const producao = dados.producao;
  const algumRodando =
    producao?.itens.some((i) => i.cronometro.status === "rodando") ?? false;
  const agora = useAgora(algumRodando) + offsetMs;

  const itens = useMemo(
    () =>
      (producao?.itens ?? []).filter(
        (i) => i.quantidadePlanejada > 0 || i.consumido > 0
      ),
    [producao]
  );
  const grupos = useMemo(() => {
    const g = agruparPorBorda(itens);
    return g.sort((a, b) => {
      const sa = ORDEM_STATUS[a.tamanhos[0]?.cronometro.status ?? "parado"];
      const sb = ORDEM_STATUS[b.tamanhos[0]?.cronometro.status ?? "parado"];
      if (sa !== sb) return sa - sb;
      const qa = a.tamanhos.reduce((s, i) => s + i.quantidadePlanejada, 0);
      const qb = b.tamanhos.reduce((s, i) => s + i.quantidadePlanejada, 0);
      return qb - qa;
    });
  }, [itens]);

  const linhasSugestao = useMemo(
    () => dados.sugestao.filter((s) => s.quantidadeSugerida > 0),
    [dados.sugestao]
  );
  const gruposSugestao = useMemo(
    () => agruparPorBorda(linhasSugestao),
    [linhasSugestao]
  );

  const fingerprint = producao
    ? grupos
        .map(
          (g) =>
            `${g.bordaChave}:${g.tamanhos.map((t) => t.tamanhoChave).join(",")}`
        )
        .join("|")
    : gruposSugestao.map((g) => g.bordaChave).join("|");
  const listaRef = useTvAutoScroll(fingerprint);

  if (!producao) {
    const totalBase = linhasSugestao.reduce((s, l) => s + l.quantidadeBase, 0);
    const totalSug = linhasSugestao.reduce(
      (s, l) => s + l.quantidadeSugerida,
      0
    );
    return (
      <Tabela scrollRef={listaRef}>
        <thead className="sticky top-0 z-10 bg-red-950/90 backdrop-blur-sm">
          <tr>
            <th className={`${TH} text-left`}>Borda</th>
            <th className={`${TH} text-left`}>Tamanho</th>
            <th className={`${TH} text-right text-white/60`}>
              Vendido {formatDiaMes(dados.dataBase)}
            </th>
            <th className={`${TH} text-right text-yellow-200`}>
              Sugestão {dados.percentual}%
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {gruposSugestao.length === 0 ? (
            <LinhaVazia colSpan={4} />
          ) : (
            gruposSugestao.flatMap((g) =>
              g.tamanhos.map((l, idx) => (
                <tr key={`${l.bordaChave}-${l.tamanhoChave}`}>
                  <td className={TD_NOME}>{idx === 0 ? g.bordaNome : ""}</td>
                  <td className={`${TD_NOME} font-bold text-white/90`}>
                    {l.tamanho}
                  </td>
                  <td className={`${TD} text-right text-white/60`}>
                    {l.quantidadeBase}
                  </td>
                  <td className={`${TD} text-right text-yellow-300`}>
                    {l.quantidadeSugerida}
                  </td>
                </tr>
              ))
            )
          )}
        </tbody>
        <tfoot className="border-t-2 border-white/30 bg-black/25">
          <tr>
            <td className={`${TD_NOME} uppercase`} colSpan={2}>
              Total
            </td>
            <td className={`${TD} text-right text-white/60`}>{totalBase}</td>
            <td className={`${TD} text-right text-yellow-300`}>{totalSug}</td>
          </tr>
        </tfoot>
      </Tabela>
    );
  }

  const tot = itens.reduce(
    (acc, i) => ({
      planejada: acc.planejada + i.quantidadePlanejada,
      consumido: acc.consumido + i.consumido,
      restante: acc.restante + i.restante,
    }),
    { planejada: 0, consumido: 0, restante: 0 }
  );

  return (
    <Tabela scrollRef={listaRef}>
      <thead className="sticky top-0 z-10 bg-red-950/90 backdrop-blur-sm">
        <tr>
          <th className={`${TH} text-left`}>Borda</th>
          <th className={`${TH} text-left`}>Tamanho</th>
          <th className={`${TH} text-right text-yellow-200`}>Produzir</th>
          <th className={`${TH} text-right`}>Saíram</th>
          <th className={`${TH} text-right text-emerald-200`}>Restam</th>
          <th className={`${TH} text-right`}>Tempo</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/10">
        {grupos.length === 0 ? (
          <LinhaVazia colSpan={6} />
        ) : (
          grupos.flatMap((g) => {
            const status = g.tamanhos[0]?.cronometro.status ?? "parado";
            const linhaCor =
              status === "rodando"
                ? "bg-emerald-700/45"
                : status === "concluido"
                  ? "bg-black/20 text-white/60"
                  : "";
            return g.tamanhos.map((item, idx) => {
              const faltou = item.excedente > 0;
              return (
                <tr key={item.id} className={linhaCor}>
                  <td className={TD_NOME}>
                    {idx === 0 ? (
                      <span className="flex items-center gap-3">
                        {status === "rodando" && (
                          <span className="relative flex h-[0.5em] w-[0.5em] shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                            <span className="relative inline-flex h-full w-full rounded-full bg-emerald-300" />
                          </span>
                        )}
                        {g.bordaNome}
                      </span>
                    ) : null}
                  </td>
                  <td className={`${TD_NOME} font-bold text-white/90`}>
                    {item.tamanho}
                  </td>
                  <td className={`${TD} text-right text-yellow-300`}>
                    {item.quantidadePlanejada}
                  </td>
                  <td className={`${TD} text-right`}>{item.consumido}</td>
                  <td
                    className={`${TD} text-right ${
                      faltou ? "text-red-300" : "text-emerald-300"
                    }`}
                  >
                    {faltou ? `-${item.excedente}` : item.restante}
                  </td>
                  <td
                    className={`${TD} text-right font-mono ${
                      status === "rodando"
                        ? "text-white"
                        : status === "pausado"
                          ? "text-amber-300"
                          : "text-white/50"
                    }`}
                  >
                    {idx === 0
                      ? status === "parado"
                        ? "—"
                        : formatDuracao(tempoItem(item, agora))
                      : ""}
                  </td>
                </tr>
              );
            });
          })
        )}
      </tbody>
      <tfoot className="border-t-2 border-white/30 bg-black/25">
        <tr>
          <td className={`${TD_NOME} uppercase`} colSpan={2}>
            Total
          </td>
          <td className={`${TD} text-right text-yellow-300`}>
            {tot.planejada}
          </td>
          <td className={`${TD} text-right`}>{tot.consumido}</td>
          <td className={`${TD} text-right text-emerald-300`}>
            {tot.restante}
          </td>
          <td />
        </tr>
      </tfoot>
    </Tabela>
  );
};

export const CLASSE_ROLAGEM_TV =
  "min-h-0 w-full overflow-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

const Tabela: React.FC<{
  children: React.ReactNode;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}> = ({ children, scrollRef }) => (
  <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm">
    <div ref={scrollRef} className={CLASSE_ROLAGEM_TV}>
      <table className="w-full divide-y divide-white/10">{children}</table>
    </div>
  </div>
);

const LinhaVazia: React.FC<{ colSpan: number }> = ({ colSpan }) => (
  <tr>
    <td
      colSpan={colSpan}
      className="px-4 py-12 text-center text-[clamp(1rem,2vw,2rem)] text-white/70"
    >
      Nenhuma borda para este dia.
    </td>
  </tr>
);

export default TvTabelaDia;
